import { OnlineStatusService, OnlineStatusType } from "ngx-online-status";
import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AuthService } from '../shared/auth.service';
import { OperatorUser } from "../shared/operator-user.model";
import { LocalStorageService } from "../shared/local-storage.service";
import { TelInputComponent } from "../tel-input/tel-input.component";
import { ToastrService } from "ngx-toastr";


let EMAIL_REGEX = '^[a-zA-Z0-9\.!#$%&\'*+\\/=?^_`{|}~-]+@([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])+\\.([a-zA-Z0-9](\\.[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])*)+$'

/**
 * Sign up from the login page. Collect all needed data, send it to server, then go to vessel-reg component.
 */
@Component({
    selector: 'app-sign-up',
    templateUrl: './sign-up.component.html',
    styleUrls: ['./sign-up.component.css'],
    standalone: false
})
export class SignUpComponent implements OnInit {
  @ViewChild('telInput') telInput!: TelInputComponent;

  signupForm = new FormGroup({
    vesselOpFirstName: new FormControl('', Validators.required),
    vesselOpLastName: new FormControl('', Validators.required),
    username: new FormControl('', [Validators.required, Validators.minLength(6), Validators.maxLength(30), Validators.pattern('(^[A-Za-z0-9][A-Za-z0-9\.\-_]+$)|(^[\w\.\+-]+@([\w-]+\.)+[\w-]{2,4}$)')]),
    email: new FormControl('', [Validators.required, Validators.pattern(EMAIL_REGEX)]),
    password: new FormControl('', [Validators.required, Validators.minLength(8), Validators.maxLength(50)]),
    phoneNumber: new FormControl('', [Validators.required, phoneNumberValidator()]),
    fishery: new FormControl('', [Validators.required]) 
  });

  vesselOpNumber = new FormControl('', [Validators.minLength(5), Validators.maxLength(8), Validators.pattern('^([a-zA-Z]|[0-9])+$')])


  usernameError: string = 'Required';
  applyPadding: boolean = false;
  hide: boolean = true;
  attemptedSend: boolean = false;
  sending = false;


  constructor(public dataSync: LocalStorageService,
    public router: Router, 
    private _snack: MatSnackBar,
     private auth: AuthService,
      private onlineStatusService: OnlineStatusService,
       private toastr: ToastrService) { }

  /**
   * Set validation and error messages.
   */
  ngOnInit(): void {
    this.signupForm.get('username')?.valueChanges.subscribe(value => {
      let e = this.signupForm.get('username')?.errors;
      if (e?.required) this.usernameError = 'Required'
      else if (e?.minlength) this.usernameError = 'Minimum 6 characters required'
      else if (e?.maxlength) this.usernameError = 'Max 30 characters allowed'
      else if (e?.pattern) this.usernameError = 'Email required or: Username must start with a letter or number followed by letters, numbers, periods, dashes, or underscores';
      else this.usernameError = '';
      this.applyPadding = e?.pattern && this.usernameError.charAt(0) == 'E';
    });
  }


  /**
   * Check validity of data entered into form, submit it to server in signup request, go to vessel registration.
   * @returns 
   */
  goToVesselRegistration() {
    if (this.onlineStatusService.getStatus() == OnlineStatusType.OFFLINE) {
      this._snack.open('No internet connection, cannot initiate sign-up', 'Dismiss', {duration: 3000});
      return;
    }

    //return;
    this.telInput.touched = true;
    this.telInput.stateChanges.next();
    this.signupForm.get('phoneNumber')?.updateValueAndValidity();
    console.log(this.signupForm);
    if (this.signupForm.valid) {
      if (this.vesselOpNumber.value && !this.vesselOpNumber.valid) {
        this._snack.open('License Number invalid', 'Dismiss', {duration: 3000});
        this.sending = false;
        return;
      }

      let form = this.signupForm.value;
      //Current sign up endpoint does not specify fields for which fishery
      //Current assumption is we're storing this only locally
      let json = {
        "userName": form.username,
        "password": form.password,
        "passwordConfirmation": form.password,
        "email": form.email,
        "roles": [
            "User"
        ],
        "phone": form.phoneNumber?.toString(),
        "firstName": form.vesselOpFirstName,
        "lastName": form.vesselOpLastName,
        "licenseNumber": this.vesselOpNumber.value,
        "properties": {
            "testdata": ""
        }
      };

      this.sending = true;
      this.auth.postSignUp(json).subscribe({
          next: (response) => {
          console.log(response);
          
          let newUser = new OperatorUser();
          newUser.setupNewUser(response);
          
          let selectedFishery = this.signupForm.get('fishery')!.value;
          newUser.groundfishFishery = 'groundfishNonTrawl' == selectedFishery || 'both' == selectedFishery;
          newUser.hmsFishery = 'hms' == selectedFishery || 'both' == selectedFishery;
          newUser.trawlFishery = 'trawl' == selectedFishery;;

          this.dataSync.storeUser(newUser, this.signupForm.get('password')!.value!).then(res => {
            if (res) {
              console.log('Sign-up success');
              this.toastr.success('', 'Sign up successful', {positionClass: 'toast-bottom-right'});
              this.dataSync.current_user.set(newUser);
              this.router.navigate(['vessel_reg', newUser.userName]);
            } else {
              alert('Could not store user, database error');
            }
          });    
        }, 
        error: (err) => { 
          this._snack.open(err.error.message, 'Dismiss', {duration: 3000});
          this.sending = false;
        }
      });

    } else {
      this.signupForm.markAllAsTouched();
      this.attemptedSend = true;
      this._snack.open('Cannot submit, check fields for errors', 'Dismiss', {duration: 3000});
      this.sending = false;
    }
    
  }

}

/**
 * Custom validator for telephone number form field
 * @returns 
 */
export function phoneNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return control.value != null && control.valid ? null : { invalid: true };
  }
}
